import { Component, Input, Output, EventEmitter, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RequestService } from '../../../management-request/services/requests.service';
import { ViewerComponent, DocViewerItem } from '../viewer/viewer.component';
import { ListCardComponent } from '../../../../core/components/list-card/list-card.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { Global } from '../../../../core/service/core/Global';
import { CatalogService } from '../../../../core/service/data/catalog.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-document-approval',
  standalone: true,
  imports: [CommonModule, ViewerComponent, ListCardComponent],
  templateUrl: './document-approval.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./document-approval.component.scss'],
})

export class DocumentApprovalComponent {
  public appSettings = inject(AppSettings);
  private requestService = inject(RequestService);
  private catalogService = inject(CatalogService);
  private lastPartyUuid: string | number = '';

  @Input() set referenceData(data: any) {
    if (!data) {
      this.lastPartyUuid = '';
      this.documents.set([]);
      return;
    }
    const partyUuid = data.party?.uuid ?? data.partyUuid ?? data.partyId ?? '';
    if (partyUuid && partyUuid === this.lastPartyUuid && this.documents().length > 0) {
      return;
    }
    this.lastPartyUuid = partyUuid;
    this.loadDocuments(data.processSlug, partyUuid);
  }

  @Input() set categoriesData(cats: any[]) {
    if (cats) {
      this.applyCategories(cats);
    } else {
      this.categories.set([]);
      this.documents.set([]);
      this.viewerItems.set([]);
    }
  }

  @Output() onReviewCompleted = new EventEmitter<void>();
  @Output() onUploadFile = new EventEmitter<{
    uuid: string;
    file: File;
    origin?: string;
    done?: (ok: boolean, res?: any) => void;
  }>();
  @Output() hasObservedChange = new EventEmitter<boolean>();
  /** True cuando todos los documentos requeridos tienen archivo cargado. */
  @Output() readinessChange = new EventEmitter<boolean>();

  documents = signal<any[]>([]);
  categories = signal<any[]>([]);
  viewerItems = signal<DocViewerItem[]>([]);
  activeDocId = signal<number | string | null>(null);
  private busySet = signal<Set<string>>(new Set());

  isViewerOpen = signal(false);

  /** Todos los requisitos con archivo (false si aún no hay lista). */
  allRequiredUploaded = computed(() => {
    const docs = this.documents();
    if (!docs.length) return false;
    return docs
      .filter((d) => d.isRequired !== false)
      .every((d) => this.hasUploadedFile(d));
  });

  hasUploadedFile(doc: any): boolean {
    return !!(doc?.filePath || doc?.fileName || doc?.fileUuid || doc?.documentUuid);
  }

  @Input() canApprove: boolean = false;
  /** Aprobación final del archivo (requiredRole BPM). Si false, canApprove solo da visto bueno intermedio. */
  @Input() canFinalApprove: boolean = false;
  @Input() canReplace: boolean = false;
  @Input() origin: string = '';
  @Input() allowObservation: boolean = true;
  /** Lista continua, sin encabezados por categoría. */
  @Input() flat: boolean = false;

  /** Puede observar / aprobar el checklist. */
  canActOnDocs(): boolean {
    return this.canApprove || this.canFinalApprove;
  }

  /** El botón verde es aprobación final (APPROVED) — solo requiredRole. */
  usesFinalApproval(): boolean {
    return this.canFinalApprove;
  }

  acceptLabel(isMobile = false): string {
    return isMobile ? 'Aceptar' : 'Aprobar archivo';
  }

  /** Solo el aprobador final aprueba el archivo → APPROVED (desde REVIEWING). */
  canAcceptDoc(doc: any): boolean {
    if (!doc || !this.usesFinalApproval()) return false;
    const s = String(doc.statusSlug || '').toUpperCase();
    return s === 'REVIEWING' || s === 'REVIEWED';
  }

  /**
   * Observar: revisor intermedio y/o aprobador final, solo en revisión.
   * Tras observar el slot queda PENDING con comentario (re-subir).
   */
  canObserveDoc(doc: any): boolean {
    if (!doc || !this.canActOnDocs() || !this.usesObservation()) return false;
    const s = String(doc.statusSlug || '').toUpperCase();
    return s === 'REVIEWING' || s === 'REVIEWED';
  }

  /** Observado: queda PENDING con comentario para re-subir/reemplazar. */
  needsResubmit(doc: any): boolean {
    if (!doc) return false;
    const s = String(doc.statusSlug || '').toUpperCase();
    if (s === 'OBSERVED') return true; // legacy
    return s === 'PENDING' && !!String(doc.reviewComment || '').trim();
  }

  // Determina si el componente permite pedir corrección
  usesObservation(): boolean {
    return this.allowObservation;
  }

  paramParty(partyUuidOrId: string | number) {
    const params: any = {};
  
   if (typeof partyUuidOrId === 'string') {
      params.partyUuid = partyUuidOrId;
    } else {
      params.partyId = partyUuidOrId;
    }
    return params;
  }

  loadDocuments(processSlug: string, partyUuidOrId: string | number) {
    const params = this.paramParty(partyUuidOrId);

    this.requestService.getDocumentsByEntity(params).subscribe({
      next: (res: any) => this._handleDocumentsResponse(res),
      error: (err: any) => console.error('Error cargando documentos', err)
    });
  }

  private _handleDocumentsResponse(res: any) {
    const dataArray = Array.isArray(res) ? res : (res.data ?? []);
    const rawCategories = dataArray[0]?.categories ?? [];
    this.applyCategories(rawCategories);
  }

  private applyCategories(rawCategories: any[]) {
    const filtered = this.filterCategories(rawCategories);
    this._normalizeDocumentIds(filtered);
    // Encabezados por dueño (Socio / Vehículo) con conteo; flat=true omite cabeceras.
    const groups = this.flat ? filtered : this.regroupByOwner(filtered);
    this.categories.set(groups);
    const flatDocs = this.processCategories(filtered, []);
    this.documents.set(flatDocs);
    this.setViewerItems(flatDocs);
    this._emitObservedState();
    this.readinessChange.emit(this.allRequiredUploaded());
  }

  /** Agrupa documentos por ownerSlug para cabeceras (área Socio / Vehículo). */
  private regroupByOwner(categories: any[]): any[] {
    const byOwner = new Map<string, any>();
    for (const cat of categories ?? []) {
      for (const doc of cat.documents ?? []) {
        const owner = String(doc.ownerSlug || cat.ownerSlug || 'PARTY').toUpperCase();
        if (!byOwner.has(owner)) {
          byOwner.set(owner, {
            docCategoryCode: owner,
            docCategoryName: this.catalogService.getOwnerName(owner) || owner,
            ownerSlug: owner,
            documents: []
          });
        }
        byOwner.get(owner).documents.push(doc);
      }
    }
    const order = ['PARTY', 'VEHICLE', 'CLAIM'];
    return [...byOwner.values()].sort((a, b) => {
      const ai = order.indexOf(a.ownerSlug);
      const bi = order.indexOf(b.ownerSlug);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
  }

  /** Incorporación/vehículo: no mezclar evidencias de siniestro. Claim: solo CLAIM. */
  private filterCategories(categories: any[]): any[] {
    const origin = String(this.origin || '').toUpperCase();
    return (categories ?? [])
      .map((cat) => ({
        ...cat,
        docCategoryCode: cat.docCategoryCode ?? cat.categorySlug,
        docCategoryName: cat.docCategoryName ?? cat.name,
        documents: (cat.documents ?? []).filter((d: any) => {
          const docOrigin = String(d.origin ?? '').toUpperCase();
          if (origin === 'CLAIM') return docOrigin === 'CLAIM' || !docOrigin;
          return docOrigin !== 'CLAIM';
        })
      }))
      .filter((cat) => (cat.documents ?? []).length > 0);
  }

  private _normalizeDocumentIds(categories: any[]) {
    categories.forEach(cat => {
      cat.documents?.forEach((doc: any) => {
        doc.id = doc.id ?? doc.uuid ?? doc.fileUuid ?? doc.filePath?.split('/').pop();
        doc.uuid = doc.uuid ?? doc.id;
        doc.origin = doc.origin || this.origin || 'PARTY';
        if (!doc.statusName) {
          doc.statusName = this.getStatusName(doc.statusSlug);
          doc.statusStyle = this.catalogService.getStatusBySlug(doc.statusSlug)?.style;
        }
      });
    });
  }

  private processCategories(rawCategories: any[], flatDocs: any[]) {
    rawCategories.forEach((cat: any) => {
      if (cat.documents) {
        flatDocs = flatDocs.concat(cat.documents);
      }
    });
    return flatDocs;
  }


  setViewerItems(flatDocs: any[]) {
    const validDocs = flatDocs.filter(d => d.filePath && (this.isImage(d) || this.isPdf(d)));

    this.viewerItems.set(validDocs.map((d: any) => {
      const pdf = this.isPdf(d);
      return {
        id: d.id,
        title: d.docTypeName ?? d.fileName ?? 'Documento',
        imageUrl: Global.url + d.filePath,
        status: this.getStatusName(d.statusSlug),
        mimeType: d.mimeType ?? (pdf ? 'application/pdf' : null),
        fileName: d.fileName ?? null,
        kind: pdf ? 'pdf' as const : 'image' as const
      };
    }));
  }

  getStatusName(slug: string): string {
    const status = this.catalogService.getStatusBySlug(slug);
    return status?.name ?? 'Pendiente';
  }

  openViewer(docId: number | string, event?: Event) {
    event?.stopPropagation();
    this.activeDocId.set(docId);
    this.isViewerOpen.set(true);
  }

  /** Tap en tarjeta móvil: con archivo abre el visor (el chevron ya indica navegación). */
  onMobileCardClick(doc: any): void {
    if (!doc?.filePath) return;
    this.openViewer(doc.id || doc.uuid);
  }

  canShowReplace(doc: any): boolean {
    return !!this.canReplace && this.needsResubmit(doc);
  }

  isPdf(doc: any): boolean {
    if (!doc) return false;
    const path = String(doc.filePath ?? '').toLowerCase();
    const name = String(doc.fileName ?? '').toLowerCase();
    const mime = String(doc.mimeType ?? '').toLowerCase();
    return path.endsWith('.pdf') || name.endsWith('.pdf') || mime.includes('pdf');
  }

  isImage(doc: any): boolean {
    if (!doc?.filePath) return false;
    const mime = String(doc.mimeType ?? '').toLowerCase();
    const name = String(doc.fileName ?? '');
    return mime.includes('image') || !!name.match(/\.(jpeg|jpg|gif|png|webp)$/i);
  }

  documentThumbSrc(doc: any): string | null {
    if (!this.isImage(doc)) return null;
    const path = doc.thumbnailPath || doc.filePath;
    return path ? Global.url + path : null;
  }

  closeViewer() {
    this.isViewerOpen.set(false);
    this.activeDocId.set(null);
  }

  onDocSelect(newDocId: number | string) {
    this.activeDocId.set(newDocId);
  }

  findDoc(docId: string | number | null | undefined) {
    if (docId == null) return null;
    return this.documents().find(d => d.id === docId || d.uuid === docId || d.documentUuid === docId) ?? null;
  }

  docKey(doc: any): string | number {
    return doc?.id ?? doc?.uuid;
  }

  getActiveDoc() {
    return this.findDoc(this.activeDocId());
  }

  approveDocument() {
    const doc = this.getActiveDoc();
    if (!doc || !this.canAcceptDoc(doc)) return;
    this.updateStatus(this.docKey(doc), 'APPROVED', undefined, undefined, {
      action: 'APPROVE',
      isFinalApproval: true
    });
  }

  async requestCorrection(doc?: any, dismiss?: () => void) {
    const target = doc || this.getActiveDoc();
    if (!target) return;
    const overlay = this.isViewerOpen()
      ? (document.querySelector('app-viewer') as HTMLElement | null)
      : null;
    const result = await Swal.fire({
      title: 'Pedir corrección de este archivo',
      html: `<p class="small text-muted mb-2">La solicitud sigue abierta. Solo se pedirá reemplazar <strong>${target.docTypeName || 'este documento'}</strong>.</p>`,
      input: 'textarea',
      inputPlaceholder: 'Indique qué está mal y qué archivo debe subir la entidad...',
      inputValidator: (value) => !String(value || '').trim() ? 'Indique el motivo de la corrección' : undefined,
      showCancelButton: true,
      confirmButtonText: 'Enviar a corrección',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d39e00',
      heightAuto: false,
      target: overlay || document.body
    });
    if (!result.isConfirmed) return;
    this.updateStatus(this.docKey(target), 'PENDING', String(result.value).trim(), dismiss, { action: 'OBSERVE' });
  }

  // Comprueba si el documento esta en proceso de subida
  isDocumentBusy(doc: any): boolean {
    const key = String(doc?.uuid ?? doc?.id ?? '');
    return this.busySet().has(key);
  }

  // Actualiza el conjunto de documentos en subida
  private setBusy(uuid: string, busy: boolean): void {
    const next = new Set(this.busySet());
    if (busy) next.add(uuid);
    else next.delete(uuid);
    this.busySet.set(next);
  }

  // Inicia reemplazo de archivo en segundo plano
  onReplaceFile(event: Event, doc: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const uuid = String(doc?.uuid ?? doc?.documentUuid ?? doc?.id ?? '');
    if (!file || !uuid || this.isDocumentBusy(doc)) return;
    const origin = (doc?.origin ?? this.origin ?? 'PARTY').toUpperCase();
    this.setBusy(uuid, true);
    this.onUploadFile.emit({
      uuid,
      file,
      origin,
      done: (ok: boolean, res?: any) => this.handleUploadDone(uuid, ok, res)
    });
  }

  // Procesa resultado de la subida en segundo plano
  private handleUploadDone(uuid: string, ok: boolean, res?: any): void {
    this.setBusy(uuid, false);
    if (!ok) return;
    this.applyUploadSuccess(uuid, res);
  }

  // Aplica los cambios de la subida exitosa en memoria
  applyUploadSuccess(docId: string, res?: any): void {
    const si = this.catalogService.getStatusBySlug('REVIEWING');
    this._syncUploadedUI(docId, si, res);
    this._emitObservedState();
    this.readinessChange.emit(this.allRequiredUploaded());
  }

  // Sincroniza las listas reactivas con el archivo subido
  private _syncUploadedUI(docId: string, si: any, res?: any): void {
    const patch = (d: any) => (!this._sameDoc(d, docId) ? d : {
      ...d,
      statusSlug: 'REVIEWING',
      statusName: si?.name ?? 'En revision',
      statusStyle: si?.style ?? 'border-info text-info',
      reviewComment: null,
      filePath: res?.filePath ?? d.filePath,
      fileName: res?.fileName ?? d.fileName,
      fileUuid: res?.fileUuid ?? res?.document?.uuid ?? d.fileUuid
    });
    this.documents.update(docs => docs.map(patch));
    this.categories.update(cats => cats.map(c => ({ ...c, documents: c.documents?.map(patch) })));
  }

  private updateStatus(
    docId: string | number,
    statusSlug: string,
    reviewComment?: string,
    dismiss?: () => void,
    extras: { action?: string; isFinalApproval?: boolean } = {}
  ) {
    const doc = this.findDoc(docId) || this.getActiveDoc();
    const origin = (doc?.origin || this.origin || 'PARTY').toUpperCase();
    const targetUuid = doc?.uuid || doc?.documentUuid || docId;

    const params: any = { id: targetUuid, statusSlug, origin };
    if (reviewComment) params.reviewComment = reviewComment;
    if (extras.action) params.action = extras.action;
    if (extras.isFinalApproval !== undefined) params.isFinalApproval = extras.isFinalApproval;

    this.requestService.updateDocument(params).subscribe({
      next: () => {
        this._syncDocUI(this.docKey(doc) ?? docId, statusSlug, reviewComment);
        this._emitObservedState();
        dismiss?.();
        if (extras.action === 'OBSERVE' || (statusSlug === 'PENDING' && reviewComment)) {
          this.closeViewer();
          this.onReviewCompleted.emit();
          Swal.fire({
            icon: 'success',
            title: 'Corrección pedida',
            text: 'La entidad debe reemplazar solo ese archivo. La solicitud sigue en la bandeja.',
            timer: 2500,
            showConfirmButton: false
          });
          return;
        }
        this._handleNavigation(this.docKey(doc) ?? docId);
      },
      error: (err) => {
        console.error('Error actualizando documento', err);
        Swal.fire('Error', err.error?.message ?? err.error?.error ?? 'No se pudo actualizar el documento.', 'error');
      }
    });
  }

  private _syncDocUI(docId: string | number, statusSlug: string, reviewComment?: string) {
    const si = this.catalogService.getStatusBySlug(statusSlug);
    this._updateFlatDocs(docId, statusSlug, si, reviewComment);
    this._updateCategoryDocs(docId, statusSlug, si, reviewComment);
    this._updateViewerDocs(docId, si);
  }

  private _sameDoc(d: any, docId: any): boolean {
    return d.id === docId || d.uuid === docId || d.documentUuid === docId;
  }

  private _updateFlatDocs(docId: any, statusSlug: string, si: any, reviewComment?: string) {
    this.documents.update(docs => docs.map(d => 
      !this._sameDoc(d, docId) ? d : {
        ...d,
        statusSlug,
        statusName: si?.name,
        statusStyle: si?.style,
        reviewComment: reviewComment ?? d.reviewComment
      }
    ));
  }

  private _updateCategoryDocs(docId: any, statusSlug: string, si: any, reviewComment?: string) {
    this.categories.update(cats => cats.map(cat => ({
      ...cat,
      documents: cat.documents?.map((d: any) => 
        !this._sameDoc(d, docId) ? d : {
          ...d,
          statusSlug,
          statusName: si?.name,
          statusStyle: si?.style,
          reviewComment: reviewComment ?? d.reviewComment
        }
      )
    })));
  }

  private _updateViewerDocs(docId: any, si: any) {
    this.viewerItems.update(items => items.map(item => 
      item.id !== docId ? item : { ...item, status: si?.name ?? 'Pendiente' }
    ));
  }

  private _handleNavigation(docId: string | number) {
    const list = this.viewerItems();
    const idx = list.findIndex(i => i.id === docId);
    if (idx < list.length - 1) {
      this.activeDocId.set(list[idx + 1].id);
    } else {
      this.checkIfAllComplete();
    }
  }

  private checkIfAllComplete() {
    const docs = this.documents();
    if (!this.usesFinalApproval()) return;
    const pending = docs.filter((d) => {
      const s = String(d.statusSlug || '').toUpperCase();
      return s === 'REVIEWING' || s === 'REVIEWED';
    });
    if (pending.length === 0) {
      this.closeViewer();
      this.onReviewCompleted.emit();
    }
  }

  // Recarga los documentos actuales de la entidad
  reloadDocuments(): void {
    if (!this.lastPartyUuid) return;
    this.loadDocuments('', this.lastPartyUuid);
  }

  // Emite si existen documentos pendientes de re-subida (observados)
  private _emitObservedState(): void {
    const hasObserved = this.documents().some((d) => this.needsResubmit(d));
    this.hasObservedChange.emit(hasObserved);
  }
}
